import type { Dispatch, SetStateAction } from 'react';
import type { ArticleImage } from '@rankhelm/shared';

/** Parsed seo_meta_json structure */
export interface ArticleSeoMeta {
  title?: string;
  description?: string;
  focus_keyword?: string;
  og_title?: string;
  og_description?: string;
  secondary_keywords?: string[];
  [key: string]: unknown;
}

interface ArticleMetadataTabProps {
  seoMeta: ArticleSeoMeta;
  selectedImage: ArticleImage | undefined;
  resolveImageUrl: (url: string | null | undefined) => string | null;
  isEditMode: boolean;
  editSeoMeta: ArticleSeoMeta | null;
  setEditSeoMeta: Dispatch<SetStateAction<ArticleSeoMeta | null>>;
}

const META_FIELDS = [
  { label: 'Meta Title', key: 'title', charLimit: 60 },
  { label: 'Meta Description', key: 'description', charLimit: 160 },
  { label: 'Focus Keyword', key: 'focus_keyword' },
  { label: 'OG Title', key: 'og_title' },
  { label: 'OG Description', key: 'og_description' },
] as const;

export function ArticleMetadataTab({
  seoMeta,
  selectedImage,
  resolveImageUrl,
  isEditMode,
  editSeoMeta,
  setEditSeoMeta,
}: ArticleMetadataTabProps) {
  return (
    <div className="bg-slate-800 rounded-[10px] p-6 border border-slate-700">
      {/* OG Image section */}
      {selectedImage && (
        <div className="mb-7">
          <h3 className="text-slate-100 mt-0 mb-4 text-base">OG Image</h3>
          <div className="mb-3">
            <img
              src={resolveImageUrl(selectedImage.r2_key || selectedImage.image_url) || ''}
              alt="OG Image"
              className="max-w-[400px] max-h-[250px] rounded-lg border border-slate-700"
            />
          </div>
          <p className="text-slate-400 text-xs mt-2 mb-1">
            OG images are 1200x630. This uses the selected featured image.
          </p>
          <p className="text-slate-500 text-xs m-0 italic">
            Regenerate OG image coming soon.
          </p>
        </div>
      )}

      <h3 className="text-slate-100 mt-0 mb-4">Metadata</h3>
      {META_FIELDS.map(item => {
        const displayMeta = isEditMode ? editSeoMeta : seoMeta;
        const value = String(displayMeta?.[item.key] || '');
        const charLimit = 'charLimit' in item ? item.charLimit : undefined;
        return (
          <div key={item.label} className="mb-4">
            <label className="text-slate-400 text-xs uppercase tracking-wide">
              {item.label}
              {charLimit && value && (
                <span className={`ml-2 ${value.length > charLimit ? 'text-red-400' : 'text-green-400'}`}>
                  ({value.length}/{charLimit})
                </span>
              )}
            </label>
            {isEditMode ? (
              charLimit ? (
                <textarea
                  value={value}
                  onChange={(e) => setEditSeoMeta(prev => prev ? { ...prev, [item.key]: e.target.value } : prev)}
                  className="w-full min-h-[80px] bg-slate-900 border-2 border-blue-500 rounded-md px-3.5 py-2.5 mt-1 text-slate-200 text-sm font-[inherit] box-border"
                />
              ) : (
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setEditSeoMeta(prev => prev ? { ...prev, [item.key]: e.target.value } : prev)}
                  className="w-full bg-slate-900 border-2 border-blue-500 rounded-md px-3.5 py-2.5 mt-1 text-slate-200 text-sm font-[inherit] box-border"
                />
              )
            ) : (
              <div className="bg-slate-900 border border-slate-700 rounded-md px-3.5 py-2.5 mt-1 text-slate-200 text-sm">
                {value || '—'}
              </div>
            )}
          </div>
        );
      })}

      {seoMeta.secondary_keywords && (
        <div className="mb-4">
          <label className="text-slate-400 text-xs uppercase tracking-wide">Secondary Keywords</label>
          <div className="flex gap-1.5 flex-wrap mt-1.5">
            {seoMeta.secondary_keywords.map((kw, i) => (
              <span key={i} className="bg-slate-700 text-slate-200 px-2.5 py-1 rounded-full text-xs">
                {kw}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
